<script setup>
const DEFAULT_ICON_URL = '/images/site/default-photo.svg';
const TEAM_ICON_URL = '/images/site/logo-lenta.svg';
const props = defineProps({
  firstTeam: {
    type: Object,
    required: true,
  },
  secondTeam: {
    type: Object,
    required: true,
  },
  showCity: {
    type: Boolean,
    default: true,
  },
  isSmall: {
    type: Boolean,
    default: false,
  },
  isHomeLogoWithOpponent: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: '',
  },
});

const imgSize = props.isSmall ? 32 : 50;
</script>

<template>
  <div class="i-teams">
    <div v-if="isHomeLogoWithOpponent && secondTeam" class="i-teams__wrapper">
      <div class="i-teams__item">
        <div class="i-teams__desc">
          <div class="i-teams__name">{{ firstTeam.name }}</div>
          <div v-if="showCity" class="i-teams__city">{{ firstTeam.city }}</div>
        </div>
        <div class="i-teams__image" :class="{'i-teams__image_small': isSmall}">
          <img
            :width="imgSize"
            :height="imgSize"
            :src="firstTeam.imageUrl || TEAM_ICON_URL"
            :alt="firstTeam.name + ' - Лого'"
          >
        </div>
      </div>

      <div class="i-teams__info" :class="{'i-teams__info_small': isSmall}">
        <strong class="i-teams__game">- : -</strong>
      </div>

      <div class="i-teams__item">
        <div class="i-teams__desc">
          <div class="i-teams__name">{{ secondTeam?.name }}</div>
          <div v-if="showCity" class="i-teams__city">{{ secondTeam?.city }}</div>
        </div>
        <div class="i-teams__image" :class="{'i-teams__image_small': isSmall}">
          <img
            :width="imgSize"
            :height="imgSize"
            :src="secondTeam?.imageUrl || DEFAULT_ICON_URL"
            :alt="secondTeam?.name + ' - Лого'"
          >
        </div>
      </div>
    </div>
    <div v-else class="i-teams__wrapper">
      <div class="i-teams__item i-teams__item_single">
        <div class="i-teams__desc">
          <div class="i-teams__name">{{ name || firstTeam.name }}</div>
          <div v-if="showCity" class="i-teams__city">{{ firstTeam.city }}</div>
        </div>
        <div class="i-teams__image" :class="{ 'i-teams__image_small': isSmall }">
          <img
            :width="imgSize"
            :height="imgSize"
            :src="firstTeam.imageUrl || TEAM_ICON_URL"
            :alt="firstTeam.name + ' - Лого'"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.i-teams {
  margin-bottom: 20px;
  flex-grow: 1;

  &_order {
    margin-bottom: 0;

    .i-teams {
      &__wrapper {
        justify-content: flex-start;
      }
    }
  }

  &_main {
    position: relative;

    .i-teams {
      &__item {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        min-width: initial;

        &:first-child {
          right: 100%;
          text-align: right;
        }

        &:last-child {
          left: 100%;
        }
      }

      &__name {
        white-space: nowrap;
      }

      &__info {
        margin-top: 0;
      }
    }
  }

  &_page {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;

    .i-teams {
      &__wrapper {
        justify-content: center;
      }

      &__item {
        border: 1px solid $border-color;
        padding: 30px;
        flex-direction: column-reverse;
        text-align: center;
        min-width: 200px;
        border-radius: $border-radius;

        &:last-child {
          flex-direction: column-reverse;

          .i-teams {
            &__image {
              margin-right: 0;
            }
          }
        }
      }

      &__game {
        font-size: 20px;
        display: block;
        margin-top: 75px;
      }

      &__info {
        .i-teams__time~.i-teams__game {
          margin-top: 0;
        }
      }

      &__image {
        width: 100px;
        height: 100px;
        margin-bottom: 20px;
      }

      &__desc {
        margin-right: 0;
      }
    }
  }

  &_subscription {
    .i-teams {
      &__link {
        display: block;
        pointer-events: none;
      }
    }
  }

  &_verification {
    margin-bottom: 0;

    .i-teams {
      &__wrapper {
        justify-content: flex-start;
      }

      &__item {
        min-width: initial;
      }
    }
  }

  &__wrapper {
    display: flex;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    max-width: 300px;
    min-width: 300px;

    &_single {
      margin: 0 auto;
      width: auto;

      &.i-teams__desc {
        width: 100%;
      }
    }

    &:last-child {
      flex-direction: row-reverse;

      .i-teams {
        &__desc {
          margin-right: 0;
          text-align: left;
        }

        &__image {
          margin-right: 20px;
        }
      }
    }
  }

  &__desc {
    margin-right: 16px;
    text-align: right;
  }

  &__name {
    color: $color;
    font-weight: 700;
    transition: color 0.3s;
    text-transform: uppercase;
    font-size: 20px;
  }

  &__city {
    color: $grey-text;
    font-size: 14px;
    text-transform: capitalize;
  }

  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
    min-width: 50px;
    min-height: 50px;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
  }

  &__image_small {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
  }

  &__info {
    min-width: 23px;
    margin: 0 30px;
    margin-top: 14px;
    color: $color;
  }

  &__info_small {
    align-self: center;
    margin: 0 15px;
  }

  &__game {
    white-space: nowrap;
  }

  &__date,
  &__time {
    color: $primary;
    font-weight: 700;
  }

  &__date {
    margin-bottom: 10px;
    font-size: 24px;
  }

  &__time {
    margin-bottom: 25px;
  }

  @media screen and (max-width: 1199px) {
    &__item {
      min-width: 150px;
      max-width: 150px;
      position: static;
      transform: initial;
    }
  }

  @media screen and (max-width: $screen-md) {
    margin-bottom: 0;

    &_main,
    &_order,
    &_subscription {
      .i-teams {
        &__wrapper {
          justify-content: space-between;
        }

        &__item {
          max-width: initial;
          width: 40%;
          position: static;
          transform: initial;
        }

        &__info {
          margin-top: 12px;
        }
      }
    }
  }

  @media (max-width: $screen-xs) {
    &_main {
      width: 100%;

      .i-teams {
        &__name {
          white-space: initial;
        }

        &__item {
          &:first-child {
            text-align: center;
          }
        }
      }
    }

    &_small {
      margin-bottom: 0;

      .i-teams {
        &__wrapper {
          align-items: center;
        }

        &__image {
          width: 20px;
          min-width: 20px;
          height: 20px;
          min-height: 20px;
          margin-bottom: 0;
        }

        &__item {
          flex-direction: row-reverse;
          max-width: initial;
          min-width: initial;
          justify-content: center;
          width: 40%;

          &:first-child {
            .i-teams {
              &__image {
                margin-right: 10px;
              }
            }
          }

          &:last-child {
            flex-direction: row;

            .i-teams {
              &__desc {
                margin-right: 10px;
              }
            }
          }
        }

        &__info {
          margin: 0 10px;
          min-width: initial;
        }

        &__name {
          font-size: 14px;
        }

        &__city {
          display: none;
        }
      }
    }

    &_subscription {
      width: 100%;

      .i-teams {
        &__item {
          &:first-child {
            text-align: center;
          }
        }
      }
    }

    &_verification {
      width: 100%;
      margin-bottom: 15px;

      .i-teams {
        &__wrapper {
          justify-content: center;
        }

        &__info {
          margin: 0 30px;
          margin-top: 12px;
        }
      }
    }

    &__item {
      flex-direction: column-reverse;
      text-align: center;
      min-width: 122px;
      max-width: 122px;

      &:first-child {
        .i-teams {
          &__desc {
            margin-right: 0;
          }
        }
      }

      &:last-child {
        flex-direction: column-reverse;

        .i-teams {
          &__image {
            margin-right: 0;
          }

          &__desc {
            text-align: center;
          }
        }
      }
    }

    &__info {
      margin: 0 10px;
      margin-top: 12px;
    }

    &__image {
      margin-bottom: 16px;
      width: 60px;
      height: 60px;
    }

    &__desc {
      margin-right: 0;
      text-align: center;
    }

    &__name {
      white-space: initial;
      font-size: 14px;
    }

    &__wrapper {
      justify-content: center;
    }
  }
}

.i-order-event-item {
  .i-teams__item {
    min-width: auto;

    &_single {
      margin: 0;
    }
  }
}

.i-order-event-item__name {
  .i-teams__item_single {
    min-width: 100%;
    max-width: 100%;
  }

  .i-teams__name {
    font-size: 16px;
  }

  .i-teams__info {
    margin: 0 20px;
    display: flex;
    align-items: center;
  }
}
</style>
